import { ContentType } from '@entries/content-types'
import ContentfulBlogArticleCard from '@entries/contentful-blog-article-card'
import ContentfulConversionArea from '@entries/contentful-conversion-area'
import ContentfulPageImageTitle from '@entries/contentful-page-image-title'
import ContentfulPageSection from '@entries/contentful-page-section'
import ContentfulStaticBlock from '@entries/contentful-static-block'
import Form from '@entries/contentful-subscription-form/form'
import { withDisplayName } from '@hocs/with-display-name'
import { Entry } from '@resolvers/entry'
import { ContentfulPageImageTitleTemplate } from '@typings/contentful-page-image-title-template'
import { ContentfulPageSectionStyle } from '@typings/contentful-page-section-template'
import { Container } from '@ui/container'
import { SectionTemplate } from '@ui/section-template'
import WaypointProvider from '@ui/waypoint-provider'
import clsx from 'clsx'
import { graphql } from 'gatsby'
import React, { Fragment } from 'react'

const ContentfulDeprecatedPageTemplate: Utils.EntryFC<
  Queries.ContentfulDeprecatedPageTemplateFragment
> = ({ content, fieldInspectorProps }) => {
  const {
    template,
    isLandingPage,
    sections = [],
    form,
    blogArticlesRefs,
    conversionArea,
  } = content
  const Wrapper = isLandingPage ? WaypointProvider : Fragment
  return (
    <div
      className={clsx(
        'group/deprecated-page-template',
        template?.includes('refresh') && 'is-refresh',
        !template?.includes('refresh') && 'is-not-refresh',
        template === ContentfulPageImageTitleTemplate.REFRESH_LANDING &&
          'is-refresh-landing',
        template !== ContentfulPageImageTitleTemplate.PODCAST &&
          'is-not-podcast',
        template === ContentfulPageImageTitleTemplate.PODCAST && 'is-podcast',
        template === ContentfulPageImageTitleTemplate.REFRESH_INFORMATIONAL &&
          'is-informational',
        form && 'has-form',
        !form && 'has-not-form'
      )}
    >
      <Wrapper>
        <div
          className={clsx(
            'relative overflow-hidden',
            'group-[.is-refresh-landing]/deprecated-page-template:laptop:pb-[120px]',
            'group-[.is-refresh.has-not-form]/deprecated-page-template:tablet:!bg-[center_bottom_170px]'
          )}
        >
          <Container
            className={clsx(
              'group-[.is-podcast]/deprecated-page-template:p-0 group-[.is-podcast]/deprecated-page-template:laptopL:max-w-none',
              'group-[.is-informational]/deprecated-page-template:p-0 group-[.is-informational]/deprecated-page-template:laptopL:max-w-none'
            )}
          >
            <div
              className={clsx(
                'grid grid-cols-1 gap-6 laptop:grid-cols-12',
                'group-[.is-not-refresh.is-not-podcast]/deprecated-page-template:pb-10 group-[.is-not-refresh.is-not-podcast]/deprecated-page-template:pt-12',
                'group-[.is-refresh.has-form.is-not-podcast]/deprecated-page-template:pt-12'
              )}
            >
              <div
                className={clsx(
                  'laptop:text-left',
                  'group-[.is-refresh.has-not-form]/deprecated-page-template:laptop:col-span-12',
                  'group-[.has-not-form]/deprecated-page-template:laptop:col-span-12',
                  'group-[.is-not-refresh.has-form]/deprecated-page-template:laptop:col-span-6',
                  'group-[.is-refresh.has-form]/deprecated-page-template:laptop:col-span-6',
                  'group-[.is-podcast]/deprecated-page-template:laptopL:p-0',
                  'group-[.is-informational]/deprecated-page-template:laptopL:p-0'
                )}
              >
                <Entry
                  content={content}
                  mapping={{
                    [ContentType.ContentfulDeprecatedPage]:
                      ContentfulPageImageTitle,
                  }}
                />
              </div>
              {form && (
                <div className="laptop:col-span-6 group-[.is-refresh]/deprecated-page-template:laptop:col-start-7">
                  <Entry
                    content={form}
                    mapping={{
                      [ContentType.ContentfulForm]: Form,
                    }}
                  />
                </div>
              )}
            </div>
          </Container>
        </div>
      </Wrapper>
      {/* page-content id is used for ab-testing DO NOT DELETE OR CHANGE */}
      <div
        className="flex flex-col"
        id="page-content"
        {...fieldInspectorProps({
          fieldId: 'sections',
        })}
      >
        {(sections || []).map((section, i) => (
          <Entry
            key={`section-${section.id}-${i}`}
            content={section}
            mapping={{
              [ContentType.ContentfulStaticBlock]: ContentfulStaticBlock,
              [ContentType.ContentfulPageSection]: ContentfulPageSection,
            }}
          />
        ))}
      </div>
      {blogArticlesRefs && (
        <SectionTemplate
          style={ContentfulPageSectionStyle.REFRESH_WHITE_BACKGROUND}
        >
          <div className="m-auto mb-[3.75rem] max-w-[776px]">
            <h2 className="mb-3 text-balance text-center text-180 leading-alternate group-[.is-refresh]/section-template:text-[2.75rem] group-[.is-refresh]/section-template:font-medium group-[.is-blue]/section-template:text-text-inverse tablet:text-200">
              L’actu financière par{' '}
              <span className="text-theme-primary-main">Fortuneo</span>
            </h2>

            <div
              {...fieldInspectorProps({
                fieldId: 'blogArticlesRefs',
              })}
            >
              {blogArticlesRefs.map((blogArticleRef) => (
                <Entry
                  key={blogArticleRef.id}
                  content={blogArticleRef}
                  mapping={{
                    [ContentType.ContentfulBlogArticle]:
                      ContentfulBlogArticleCard,
                  }}
                />
              ))}
            </div>
          </div>
        </SectionTemplate>
      )}

      {conversionArea && (
        <div
          {...fieldInspectorProps({
            fieldId: 'conversionArea',
          })}
        >
          <Entry
            content={conversionArea}
            mapping={{
              [ContentType.ContentfulConversionArea]: ContentfulConversionArea,
            }}
          />
        </div>
      )}
    </div>
  )
}

export const query = graphql`
  fragment ContentfulDeprecatedPageTemplate on Contentful3TUjMz7TxKWiQYygwIysig {
    ...ContentfulEntry
    ...ContentfulPageImageTitle
    template
    pageUrl
    headline {
      rich
      text
    }
    ctaRefs {
      ...ContentfulCtaMain
    }
    sections {
      ...ContentfulStaticBlock
      ...ContentfulPageSection
    }
    footnote {
      rich
      references {
        ...ContentfulPDFLink
      }
    }
    blogArticlesRefs {
      ...ContentfulBlogArticleCard
    }
    conversionArea {
      ...ContentfulConversionArea
    }
    isLandingPage
    form {
      ...ContentfulForm
    }
    breadcrumbsLabel
    breadcrumbs: getParentPages {
      pageUrl
      headline {
        text
      }
      breadcrumbsLabel
    }
  }
`

export default withDisplayName(
  ContentfulDeprecatedPageTemplate,
  'ContentfulDeprecatedPageTemplate'
)
